import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
  Button,
  Checkbox,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { YouTubeSubscription } from '../../types/api.types';
import { useNavigate } from 'react-router-dom';
import { getYouTubeChannelUrl } from '../../services/channelService';
import { Delete, Edit } from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';

interface GroupAccordionProps {
  groupName: string;
  groupId?: string; // Added group ID for navigation
  subscriptions: YouTubeSubscription[];
  isUngrouped?: boolean;
  onRemoveFromGroup?: (channelId: string) => void;
  onAddChannels?: () => void;
  // When rendering the Ungrouped section, allows adding a channel to one or more groups
  onAddToGroups?: (channelId: string) => void;
  expanded: boolean;
  onChange: () => void;
  onDeleteGroup?: () => void;
  onEditGroupName?: () => void;
  // Bulk selection props (only used when isUngrouped)
  isBulkMode?: boolean;
  selectedIds?: string[];
  onStartBulkSelect?: () => void;
  onCancelBulkSelect?: () => void;
  onToggleSelect?: (channelId: string) => void;
  onToggleSelectAll?: () => void;
  allSelected?: boolean;
  onConfirmBulkAssign?: () => void;
  refreshSubscriptions?: () => void;
}

export const GroupAccordion: React.FC<GroupAccordionProps> = ({
  groupName,
  groupId,
  subscriptions,
  isUngrouped = false,
  onRemoveFromGroup,
  onAddChannels,
  onAddToGroups,
  expanded,
  onChange,
  onDeleteGroup,
  onEditGroupName,
  isBulkMode = false,
  selectedIds = [],
  onStartBulkSelect,
  onCancelBulkSelect,
  onToggleSelect,
  onToggleSelectAll,
  allSelected = false,
  onConfirmBulkAssign,
  refreshSubscriptions,
}) => {
  const navigate = useNavigate();
  const { data: userData, isLoading: isUserDataLoading } = useUserData();

  const formatSubscriptionDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const handleOpenChannel = (subscription: YouTubeSubscription) => {
    const url = getYouTubeChannelUrl(subscription.channelId, subscription.channel.customUrl);
    window.open(url, '_blank');
  };

  const handleWatchGroup = () => {
    if (!groupId) {
      console.error('Cannot navigate to watch page: group ID is missing');
      return;
    }
    navigate(`/watch/${groupId}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const accordionSlots: any = {};
  if (isUngrouped) {
    accordionSlots.slotProps = {
      transition: {
        timeout: 0,
      },
    };
  }

  const showBulkControls = isUngrouped && subscriptions.length > 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      {...accordionSlots}
      data-cy="group-accordion"
      data-group-id={groupId || ''}
      data-group-name={groupName}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${groupName}-content`}
        id={`${groupName}-header`}
        data-cy="group-accordion-summary"
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" component="div">
            {groupName}
          </Typography>
          <Chip
            label={`${subscriptions.length} channel${subscriptions.length !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
          {isUngrouped && subscriptions.length > 0 && (
            <Chip label="Ungrouped" size="small" color="warning" variant="outlined" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {!isUngrouped && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={onAddChannels}
                data-cy="group-add-channels-btn"
              >
                Add Channels
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Edit />}
                onClick={onEditGroupName}
                data-cy="group-edit-name-btn"
              >
                Edit Group Name
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={handleWatchGroup}
                disabled={subscriptions.length === 0 || !groupId}
                data-cy="group-watch-btn"
              >
                Go to Recent Videos
              </Button>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<Delete />}
              onClick={onDeleteGroup}
              data-cy="group-delete-btn"
            >
              Delete Group
            </Button>
          </Box>
        )}

        {isUngrouped && showBulkControls && (
          <Box display="flex" gap={1} alignItems="center" mb={2}>
            {!isUserDataLoading && userData?.pro && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshSubscriptions}
                disabled={subscriptions.length === 0}
                data-cy="refresh-subscriptions-btn"
              >
                Refresh Subscriptions
              </Button>
            )}
            {!isBulkMode ? (
              <Button variant="outlined" size="small" onClick={onStartBulkSelect}>
                Select channels
              </Button>
            ) : (
              <>
                <Button variant="text" size="small" onClick={onToggleSelectAll}>
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onConfirmBulkAssign}
                  disabled={selectedIds.length === 0}
                >
                  Add to groups
                </Button>
                <Button variant="outlined" size="small" color="inherit" onClick={onCancelBulkSelect}>
                  Cancel
                </Button>
              </>
            )}
          </Box>
        )}

        {subscriptions.length === 0 ? (
          <Box p={2} textAlign="center">
            <Typography color="text.secondary">
              {isUngrouped ? 'All channels are organized in groups!' : 'No channels in this group yet'}
            </Typography>
          </Box>
        ) : (
          <List>
            {subscriptions.map(subscription => {
              const checked = selectedIds.includes(subscription.channelId);
              return (
                <ListItem
                  key={subscription.id}
                  divider
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => isBulkMode && onToggleSelect && onToggleSelect(subscription.channelId)}
                >
                  {isUngrouped && isBulkMode && (
                    <Checkbox
                      edge="start"
                      checked={checked}
                      onClick={e => e.stopPropagation()}
                      onChange={() => onToggleSelect?.(subscription.channelId)}
                      tabIndex={-1}
                      disableRipple
                    />
                  )}
                  <ListItemAvatar>
                    <Avatar
                      src={subscription.channel.thumbnailUrl}
                      alt={subscription.channel.title}
                      sx={{ width: 48, height: 48 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight="medium">
                        {subscription.channel.title}
                      </Typography>
                    }
                    secondary={
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Subscribed: {formatSubscriptionDate(subscription.subscribedAt)}
                        </Typography>
                      </Box>
                    }
                    sx={{ pr: 2, userSelect: isBulkMode ? 'none' : 'auto' }}
                  />
                  <Box display="flex" gap={1} alignItems="center">
                    {isUngrouped && !isBulkMode && onAddToGroups && (
                      <Tooltip title="Add to groups">
                        <IconButton
                          size="small"
                          onClick={() => onAddToGroups(subscription.channelId)}
                          color="primary"
                          data-cy="add-to-groups-btn"
                        >
                          <GroupAddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Open channel">
                      <IconButton
                        size="small"
                        onClick={e => {
                          handleOpenChannel(subscription);
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        color="primary"
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                    {!isUngrouped && onRemoveFromGroup && (
                      <Tooltip title="Remove from group">
                        <IconButton
                          size="small"
                          onClick={() => onRemoveFromGroup(subscription.channelId)}
                          color="error"
                          data-cy="remove-from-group-btn"
                        >
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
